/**
 * AdminPanel.jsx — HealHub Admin Panel (Production)
 *
 * Fixes & enhancements:
 *  ✅ Table name fixed: mentor_reports (was "reports" in v5 — bug)
 *  ✅ Notification table fixed: mentor_notifications (was "notifications" in v5)
 *  ✅ Interview email now triggers backend SMTP route
 *  ✅ Document viewer: generates signed URL for private bucket files
 *  ✅ Archive: approved/rejected apps hidden from active list (toggle)
 *  ✅ Block mentor (with reason) + Unblock
 *  ✅ Delete mentor — now requires typing "delete" instead of mentor's name (easier, avoids case issues)
 *  ✅ Analytics tab preserved (AnalyticsPanel import intact)
 *  ✅ Applications filtered by is_archived by default
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { auth }    from "../firebase";
import { supabase } from "../supabase";
import AnalyticsPanel from "../pages/AnalyticsPanel";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || "";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const css = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  .adm-root{font-family:'DM Sans',sans-serif;min-height:100vh;background:#f4f5fb;-webkit-font-smoothing:antialiased}
  .adm-header{background:linear-gradient(135deg,#1a237e,#3949ab);padding:16px 20px;display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:100}
  .adm-header h1{color:#fff;font-size:1.1rem;font-weight:700}
  .adm-header-right{display:flex;gap:8px}
  .adm-hbtn{padding:7px 14px;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.3);border-radius:8px;color:#fff;font-size:.8rem;font-weight:600;cursor:pointer}
  .adm-tabs{background:#fff;border-bottom:1px solid #e8eaf6;display:flex;overflow-x:auto;position:sticky;top:58px;z-index:99}
  .adm-tabs::-webkit-scrollbar{display:none}
  .adm-tab{padding:13px 18px;border:none;background:none;font-family:'DM Sans',sans-serif;font-size:.83rem;font-weight:600;color:#888;cursor:pointer;border-bottom:2.5px solid transparent;white-space:nowrap;transition:all .15s}
  .adm-tab.active{color:#3949ab;border-bottom-color:#3949ab}
  .adm-content{max-width:1100px;margin:0 auto;padding:20px 16px 80px}
  .adm-stats{display:grid;grid-template-columns:repeat(2,1fr);gap:12px;margin-bottom:20px}
  @media(min-width:600px){.adm-stats{grid-template-columns:repeat(4,1fr)}}
  .adm-stat{background:#fff;border-radius:12px;padding:16px;text-align:center;box-shadow:0 2px 8px rgba(57,73,171,.08);border:1px solid #e8eaf6}
  .adm-stat-icon{font-size:1.4rem}
  .adm-stat-val{font-size:1.5rem;font-weight:700;color:#1a1a2e}
  .adm-stat-label{font-size:.72rem;color:#888;margin-top:3px}
  .adm-table-wrap{background:#fff;border-radius:12px;box-shadow:0 2px 8px rgba(57,73,171,.08);border:1px solid #e8eaf6;overflow:hidden;margin-bottom:16px}
  .adm-table{width:100%;border-collapse:collapse;min-width:640px}
  .adm-table-scroll{overflow-x:auto}
  .adm-table th{padding:10px 14px;text-align:left;font-size:.75rem;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:.05em;background:#f8f9ff;border-bottom:1px solid #e8eaf6;white-space:nowrap}
  .adm-table td{padding:11px 14px;font-size:.85rem;color:#333;border-bottom:1px solid #f5f5f5;vertical-align:middle}
  .adm-table tr:last-child td{border:none}
  .adm-table tr:hover td{background:#f8f9ff}
  .adm-badge{padding:3px 10px;border-radius:20px;font-size:.72rem;font-weight:700;display:inline-flex}
  .adm-badge.pending{background:#fff3e0;color:#e65100}
  .adm-badge.approved{background:#e8f5e9;color:#2e7d32}
  .adm-badge.rejected{background:#ffebee;color:#c62828}
  .adm-badge.interview{background:#e8eaf6;color:#3949ab}
  .adm-badge.banned{background:#ffebee;color:#c62828}
  .adm-badge.active{background:#e8f5e9;color:#2e7d32}
  .adm-badge.linkedin{background:#e8f4fd;color:#0077b5}
  .adm-actions{display:flex;gap:6px;flex-wrap:wrap}
  .adm-btn{padding:5px 12px;border:none;border-radius:6px;font-size:.78rem;font-weight:600;cursor:pointer;white-space:nowrap}
  .adm-btn.approve{background:#e8f5e9;color:#2e7d32}
  .adm-btn.reject{background:#ffebee;color:#c62828}
  .adm-btn.interview{background:#e8eaf6;color:#3949ab}
  .adm-btn.ban{background:#ffebee;color:#c62828}
  .adm-btn.unban{background:#e8f5e9;color:#2e7d32}
  .adm-btn.view{background:#f0f0f0;color:#555}
  .adm-btn.delete{background:#ffebee;color:#b71c1c;border:1px solid #ffcdd2}
  .adm-btn.resolve{background:#e8f5e9;color:#2e7d32}
  .adm-btn.dismiss{background:#f5f5f5;color:#888}
  .adm-search{padding:10px 14px;border:1.5px solid #e8eaf6;border-radius:9px;font-size:.9rem;outline:none;width:100%;background:#f8f9ff;margin-bottom:14px}
  .adm-search:focus{border-color:#3949ab}
  .adm-toolbar{display:flex;gap:10px;align-items:center;margin-bottom:14px;flex-wrap:wrap}
  .adm-toolbar .adm-search{margin-bottom:0;flex:1;min-width:200px}
  .adm-toggle{display:flex;align-items:center;gap:6px;font-size:.82rem;color:#666;cursor:pointer;white-space:nowrap}
  .adm-toggle input{accent-color:#3949ab;width:14px;height:14px;cursor:pointer}
  .adm-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px}
  .adm-modal{background:#fff;border-radius:16px;width:100%;max-width:520px;padding:24px;max-height:90vh;overflow-y:auto}
  .adm-modal h3{font-size:1.1rem;font-weight:700;margin-bottom:16px;color:#1a1a2e}
  .adm-modal textarea,.adm-modal input[type=text]{width:100%;padding:10px 13px;border:1.5px solid #e8eaf6;border-radius:9px;font-size:.88rem;font-family:'DM Sans',sans-serif;outline:none;resize:vertical}
  .adm-modal textarea{min-height:80px}
  .adm-modal-actions{display:flex;gap:10px;margin-top:16px}
  .adm-modal-btn{flex:1;padding:11px;border:none;border-radius:9px;font-weight:700;font-size:.9rem;cursor:pointer}
  .adm-modal-btn.primary{background:#3949ab;color:#fff}
  .adm-modal-btn.danger{background:#f44336;color:#fff}
  .adm-modal-btn.cancel{background:#f0f0f0;color:#555}
  .adm-doc-modal{background:#fff;border-radius:16px;width:100%;max-width:720px;max-height:90vh;display:flex;flex-direction:column}
  .adm-doc-header{padding:16px 20px;border-bottom:1px solid #e8eaf6;display:flex;align-items:center;justify-content:space-between;flex-shrink:0}
  .adm-doc-body{flex:1;overflow:hidden;display:flex;flex-direction:column}
  .adm-doc-iframe{flex:1;border:none;width:100%;min-height:480px}
  .adm-empty{text-align:center;padding:40px;color:#bbb}
  .adm-spin{width:16px;height:16px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;border-radius:50%;animation:adm-s .6s linear infinite;display:inline-block}
  @keyframes adm-s{to{transform:rotate(360deg)}}
  .adm-toast{position:fixed;bottom:24px;right:24px;background:#1a237e;color:#fff;padding:12px 20px;border-radius:10px;font-size:.88rem;font-weight:600;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,.2);animation:adm-ti .3s ease}
  @keyframes adm-ti{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
`;

export default function AdminPanel() {
  const navigate = useNavigate();

  const [activeTab,     setActiveTab]     = useState("applications");
  const [applications,  setApplications]  = useState([]);
  const [mentors,       setMentors]       = useState([]);
  const [reports,       setReports]       = useState([]);
  const [payments,      setPayments]      = useState([]);
  const [stats,         setStats]         = useState({});
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [showArchived,  setShowArchived]  = useState(false);
  const [modal,         setModal]         = useState(null);
  const [modalNote,     setModalNote]     = useState("");
  const [modalConfirm,  setModalConfirm]  = useState(""); // for delete confirmation
  const [actionLoading, setActionLoading] = useState(false);
  const [docModal,      setDocModal]      = useState(null); // { url, name }
  const [toast,         setToast]         = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user || user.email !== ADMIN_EMAIL) {
      alert("Unauthorized access");
      navigate("/dashboard", { replace: true });
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = useCallback((msg, type = "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [appsRes, mentorsRes, reportsRes, paymentsRes] = await Promise.all([
        supabase.from("mentor_applications")
          .select("id,full_name,email,city,category,experience,status,degree_certificate_url,resume_url,linkedin_verified,linkedin_id,is_archived,created_at,admin_note")
          .order("created_at", { ascending: false }),
        supabase.from("mentors")
          .select("id,full_name,email,category,rating,total_reviews,sessions_count,report_count,is_active,is_banned,ban_reason,is_on_break,created_at")
          .order("created_at", { ascending: false }),
        // ✅ FIX: was "reports" in v5 — correct table is mentor_reports
        supabase.from("mentor_reports")
          .select("*, mentor:mentor_id(full_name,email)")
          .order("created_at", { ascending: false }),
        supabase.from("payments")
          .select("id,razorpay_order_id,user_email,amount,status,created_at")
          .order("created_at", { ascending: false })
          .limit(100),
      ]);

      setApplications(appsRes.data   || []);
      setMentors(mentorsRes.data     || []);
      setReports(reportsRes.data     || []);
      setPayments(paymentsRes.data   || []);
      setStats({
        totalApps:      (appsRes.data   || []).length,
        pendingApps:    (appsRes.data   || []).filter(a => a.status === "pending" && !a.is_archived).length,
        activeMentors:  (mentorsRes.data || []).filter(m => m.is_active && !m.is_banned).length,
        pendingReports: (reportsRes.data || []).filter(r => r.status === "pending").length,
        totalRevenue:   (paymentsRes.data || []).filter(p => p.status === "paid").reduce((s, p) => s + (p.amount || 0), 0),
      });
    } catch (err) {
      console.error("loadAll error:", err);
      showToast("Failed to load data. Please refresh.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Update application status via backend (triggers email) ──
  const updateApplicationStatus = async (id, status, note) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor-application/update-status`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          applicationId: id,
          status,
          note:       note || "",
          adminEmail: auth.currentUser?.email,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Update failed");
      }

      // Update local state
      setApplications(prev => prev.map(a =>
        a.id === id ? { ...a, status, admin_note: note || "", is_archived: status === "approved" || status === "rejected" } : a
      ));

      showToast(`Application ${status} ✅ — Email sent to applicant`);
    } catch (err) {
      showToast(err.message || "Update failed", "error");
    } finally {
      setActionLoading(false);
      setModal(null);
      setModalNote("");
    }
  };

  // ── Toggle ban via backend ────────────────────────────────────
  const toggleBanMentor = async (mentor) => {
    setActionLoading(true);
    try {
      const endpoint = mentor.is_banned ? "/api/mentor/unblock" : "/api/mentor/block";
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          mentorId:   mentor.id,
          reason:     modalNote || "Violation of platform policies",
          adminEmail: auth.currentUser?.email,
        }),
      });

      if (!res.ok) throw new Error("Action failed");

      setMentors(prev => prev.map(m =>
        m.id === mentor.id ? { ...m, is_banned: !m.is_banned, is_active: m.is_banned } : m
      ));
      showToast(mentor.is_banned ? "Mentor unblocked ✅" : "Mentor blocked 🔴");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setActionLoading(false);
      setModal(null);
      setModalNote("");
    }
  };

  // ── Delete mentor — requires typing "delete" (not the name) ───
  const deleteMentor = async (mentor) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor/delete`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          mentorId:    mentor.id,
          confirmName: modalConfirm,  // now expects "delete"
          adminEmail:  auth.currentUser?.email,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Delete failed");

      setMentors(prev => prev.filter(m => m.id !== mentor.id));
      showToast("Mentor account permanently deleted 🗑️");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setActionLoading(false);
      setModal(null);
      setModalConfirm("");
    }
  };

  const updateReport = async (id, status, note) => {
    const { error } = await supabase.from("mentor_reports")
      .update({ status, admin_note: note })
      .eq("id", id);
    if (!error) {
      setReports(prev => prev.map(r => r.id === id ? { ...r, status, admin_note: note } : r));
      showToast("Report updated ✅");
    }
    setModal(null);
  };

  // ── Generate signed URL for private document ─────────────────
  const viewDocument = async (rawPath, fileName) => {
    if (!rawPath) return;

    // rawPath format: "mentor-documents/applications/email/filename.pdf"
    const pathParts  = rawPath.replace("mentor-documents/", "");

    const { data, error } = await supabase.storage
      .from("mentor-documents")
      .createSignedUrl(pathParts, 3600); // 1-hour expiry

    if (error || !data?.signedUrl) {
      showToast("Could not load document. Please try again.", "error");
      return;
    }

    setDocModal({ url: data.signedUrl, name: fileName || "Document" });
  };

  const filtered = (arr) => {
    if (!search) return arr;
    const q = search.toLowerCase();
    return arr.filter(item =>
      Object.values(item).some(v => typeof v === "string" && v.toLowerCase().includes(q))
    );
  };

  // Applications shown: filter archived unless toggle is on
  const visibleApplications = filtered(applications).filter(a =>
    showArchived ? true : !a.is_archived
  );

  if (loading) return (
    <>
      <style>{css}</style>
      <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",fontFamily:"'DM Sans',sans-serif",flexDirection:"column",gap:12 }}>
        <div style={{ width:36,height:36,border:"3px solid #e8eaf6",borderTopColor:"#3949ab",borderRadius:"50%",animation:"adm-s .6s linear infinite" }} />
        <span style={{ color:"#888" }}>Loading admin panel…</span>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="adm-root">
        <div className="adm-header">
          <h1>🛡️ HealHub Admin</h1>
          <div className="adm-header-right">
            <button className="adm-hbtn" onClick={loadAll}>↻ Refresh</button>
            <button className="adm-hbtn" onClick={() => navigate("/dashboard")}>← Back</button>
          </div>
        </div>

        <div className="adm-tabs">
          {[
            { id: "applications", label: `📋 Applications (${stats.pendingApps || 0})` },
            { id: "mentors",      label: `👩‍⚕️ Mentors (${stats.activeMentors || 0})` },
            { id: "reports",      label: `🚩 Reports (${stats.pendingReports || 0})` },
            { id: "payments",     label: "💳 Payments" },
            { id: "analytics",    label: "📈 Analytics" },
          ].map(t => (
            <button key={t.id} className={`adm-tab ${activeTab === t.id ? "active" : ""}`}
              onClick={() => setActiveTab(t.id)}>{t.label}</button>
          ))}
        </div>

        <div className="adm-content">

          {/* ── Analytics (preserved) ── */}
          {activeTab === "analytics" && <AnalyticsPanel />}

          {activeTab !== "analytics" && (
            <>
              <div className="adm-stats">
                {[
                  { icon:"📋", val: stats.totalApps || 0,      label:"Total Applications" },
                  { icon:"👩‍⚕️", val: stats.activeMentors || 0,  label:"Active Mentors" },
                  { icon:"🚩", val: stats.pendingReports || 0, label:"Pending Reports" },
                  { icon:"💰", val: `₹${stats.totalRevenue || 0}`, label:"Revenue" },
                ].map(s => (
                  <div key={s.label} className="adm-stat">
                    <div className="adm-stat-icon">{s.icon}</div>
                    <div className="adm-stat-val">{s.val}</div>
                    <div className="adm-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="adm-toolbar">
                <input className="adm-search" placeholder={`Search ${activeTab}…`}
                  value={search} onChange={e => setSearch(e.target.value)} />
                {activeTab === "applications" && (
                  <label className="adm-toggle">
                    <input type="checkbox" checked={showArchived}
                      onChange={e => setShowArchived(e.target.checked)} />
                    Show archived
                  </label>
                )}
              </div>
            </>
          )}

          {/* ── Applications ── */}
          {activeTab === "applications" && (
            <div className="adm-table-wrap">
              <div className="adm-table-scroll">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Category</th>
                      <th>Exp</th><th>LinkedIn</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleApplications.length === 0 ? (
                      <tr><td colSpan={7} className="adm-empty">No applications found</td></tr>
                    ) : visibleApplications.map(a => (
                      <tr key={a.id} style={{ opacity: a.is_archived ? .6 : 1 }}>
                        <td>
                          <strong>{a.full_name}</strong>
                          <br /><span style={{ fontSize:".73rem",color:"#888" }}>{a.city}</span>
                        </td>
                        <td>{a.email}</td>
                        <td>{a.category || "general"}</td>
                        <td>{a.experience} yrs</td>
                        <td>
                          {a.linkedin_verified
                            ? <span className="adm-badge linkedin">🔗 Verified</span>
                            : <span style={{ fontSize:".75rem",color:"#bbb" }}>—</span>
                          }
                        </td>
                        <td><span className={`adm-badge ${a.status}`}>{a.status}</span></td>
                        <td>
                          <div className="adm-actions">
                            {(a.degree_certificate_url || a.resume_url) && (
                              <button className="adm-btn view"
                                onClick={() => viewDocument(a.degree_certificate_url, "Degree Certificate")}>
                                📎 Degree
                              </button>
                            )}
                            {a.resume_url && (
                              <button className="adm-btn view"
                                onClick={() => viewDocument(a.resume_url, "Resume / CV")}>
                                📄 Resume
                              </button>
                            )}
                            {!a.is_archived && a.status !== "approved" && (
                              <button className="adm-btn approve"
                                onClick={() => setModal({ type:"approve", app: a })}>Approve</button>
                            )}
                            {!a.is_archived && a.status !== "interview" && (
                              <button className="adm-btn interview"
                                onClick={() => setModal({ type:"interview", app: a })}>Interview</button>
                            )}
                            {!a.is_archived && a.status !== "rejected" && (
                              <button className="adm-btn reject"
                                onClick={() => setModal({ type:"reject", app: a })}>Reject</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Mentors ── */}
          {activeTab === "mentors" && (
            <div className="adm-table-wrap">
              <div className="adm-table-scroll">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Category</th>
                      <th>Rating</th><th>Sessions</th><th>Reports</th>
                      <th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered(mentors).length === 0 ? (
                      <tr><td colSpan={8} className="adm-empty">No mentors found</td></tr>
                    ) : filtered(mentors).map(m => (
                      <tr key={m.id}>
                        <td><strong>{m.full_name}</strong></td>
                        <td style={{ fontSize:".82rem" }}>{m.email}</td>
                        <td>{m.category || "general"}</td>
                        <td>{m.total_reviews >= 3 ? `${m.rating}★` : "New"}</td>
                        <td>{m.sessions_count || 0}</td>
                        <td style={{ color: m.report_count >= 15 ? "#f44336" : "#333", fontWeight: m.report_count >= 15 ? 700 : 400 }}>
                          {m.report_count || 0}
                        </td>
                        <td>
                          <span className={`adm-badge ${m.is_banned ? "banned" : m.is_on_break ? "pending" : m.is_active ? "active" : "pending"}`}>
                            {m.is_banned ? "Banned" : m.is_on_break ? "On Break" : m.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="adm-actions">
                            {m.is_banned
                              ? <button className="adm-btn unban" onClick={() => setModal({ type:"unban", mentor: m })}>Unban</button>
                              : <button className="adm-btn ban"   onClick={() => setModal({ type:"ban",   mentor: m })}>Ban</button>
                            }
                            <button className="adm-btn view"
                              onClick={() => supabase.from("mentors")
                                .update({ is_active: !m.is_active }).eq("id", m.id)
                                .then(() => { setMentors(prev => prev.map(x => x.id === m.id ? { ...x, is_active: !x.is_active } : x)); showToast("Updated"); })
                              }>
                              {m.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button className="adm-btn delete"
                              onClick={() => { setModal({ type:"delete", mentor: m }); setModalConfirm(""); }}>
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Reports ── */}
          {activeTab === "reports" && (
            <div className="adm-table-wrap">
              <div className="adm-table-scroll">
                <table className="adm-table">
                  <thead>
                    <tr><th>Mentor</th><th>Category</th><th>Description</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {filtered(reports).length === 0 ? (
                      <tr><td colSpan={6} className="adm-empty">No reports found</td></tr>
                    ) : filtered(reports).map(r => (
                      <tr key={r.id}>
                        <td>
                          <strong>{r.mentor?.full_name || "Unknown"}</strong>
                          <br /><span style={{ fontSize:".73rem",color:"#888" }}>{r.mentor?.email}</span>
                        </td>
                        <td>{r.category?.replace(/_/g," ")}</td>
                        <td style={{ maxWidth:200 }}>
                          <span style={{ fontSize:".8rem",color:"#666" }}>{r.description?.slice(0,80) || "—"}</span>
                        </td>
                        <td><span className={`adm-badge ${r.status}`}>{r.status}</span></td>
                        <td style={{ fontSize:".78rem" }}>{new Date(r.created_at).toLocaleDateString()}</td>
                        <td>
                          <div className="adm-actions">
                            {r.status === "pending" && (
                              <>
                                <button className="adm-btn resolve" onClick={() => setModal({ type:"resolveReport", report: r })}>Resolve</button>
                                <button className="adm-btn dismiss" onClick={() => updateReport(r.id,"dismissed","Dismissed by admin")}>Dismiss</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Payments ── */}
          {activeTab === "payments" && (
            <div className="adm-table-wrap">
              <div className="adm-table-scroll">
                <table className="adm-table">
                  <thead>
                    <tr><th>Order ID</th><th>User</th><th>Amount</th><th>Status</th><th>Date</th></tr>
                  </thead>
                  <tbody>
                    {filtered(payments).length === 0 ? (
                      <tr><td colSpan={5} className="adm-empty">No payments found</td></tr>
                    ) : filtered(payments).map(p => (
                      <tr key={p.id}>
                        <td style={{ fontSize:".78rem",fontFamily:"monospace" }}>{p.razorpay_order_id?.slice(-12) || "—"}</td>
                        <td>{p.user_email}</td>
                        <td style={{ fontWeight:700 }}>₹{p.amount}</td>
                        <td>
                          <span className={`adm-badge ${p.status === "paid" ? "approved" : p.status === "refunded" ? "rejected" : "pending"}`}>
                            {p.status}
                          </span>
                        </td>
                        <td style={{ fontSize:".78rem" }}>{new Date(p.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Action Modal ── */}
      {modal && (
        <div className="adm-modal-overlay" onClick={e => e.target === e.currentTarget && !actionLoading && setModal(null)}>
          <div className="adm-modal">
            <h3>
              {modal.type === "approve"       ? "✅ Approve Application" :
               modal.type === "reject"        ? "❌ Reject Application" :
               modal.type === "interview"     ? "📞 Schedule Interview" :
               modal.type === "ban"           ? "🔴 Ban Mentor" :
               modal.type === "unban"         ? "✅ Unban Mentor" :
               modal.type === "delete"        ? "🗑️ Delete Mentor Account" :
               modal.type === "resolveReport" ? "✅ Resolve Report" : "Action"}
            </h3>

            {modal.app && (
              <p style={{ fontSize:".85rem",color:"#666",marginBottom:12 }}>
                Applicant: <strong>{modal.app.full_name}</strong> ({modal.app.email})
              </p>
            )}
            {modal.mentor && (
              <p style={{ fontSize:".85rem",color:"#666",marginBottom:12 }}>
                Mentor: <strong>{modal.mentor.full_name}</strong> ({modal.mentor.email})
              </p>
            )}

            {/* Delete requires typing "delete" (not the name) */}
            {modal.type === "delete" ? (
              <>
                <div style={{ background:"#ffebee",padding:"10px 14px",borderRadius:8,marginBottom:14,fontSize:".82rem",color:"#c62828" }}>
                  ⚠️ This is <strong>permanent and irreversible</strong>. All data, sessions, and storage files will be deleted.
                </div>
                <label style={{ fontSize:".78rem",fontWeight:600,color:"#666",display:"block",marginBottom:6 }}>
                  Type <strong style={{ color:"#c62828" }}>delete</strong> to confirm:
                </label>
                <input type="text" value={modalConfirm}
                  onChange={e => setModalConfirm(e.target.value)}
                  placeholder="delete"
                  style={{ width:"100%",padding:"10px 13px",border:"1.5px solid #e8eaf6",borderRadius:9,fontSize:".88rem",fontFamily:"'DM Sans',sans-serif",outline:"none",marginBottom:8 }} />
              </>
            ) : (
              <>
                <label style={{ fontSize:".78rem",fontWeight:600,color:"#666",display:"block",marginBottom:6 }}>
                  {modal.type === "ban"           ? "Reason for ban *" :
                   modal.type === "interview"     ? "Custom message to applicant *" :
                   modal.type === "resolveReport" ? "Resolution note" :
                   "Note to applicant (optional)"}
                </label>
                <textarea value={modalNote} onChange={e => setModalNote(e.target.value)}
                  placeholder={
                    modal.type === "interview"
                      ? "e.g. Hi! We'd like to schedule a 20-min call. Please reply with your availability this week…"
                      : "Add a note…"
                  } />
              </>
            )}

            <div className="adm-modal-actions">
              <button className="adm-modal-btn cancel"
                onClick={() => { setModal(null); setModalNote(""); setModalConfirm(""); }}>
                Cancel
              </button>
              <button
                className={`adm-modal-btn ${modal.type === "delete" || modal.type === "reject" || modal.type === "ban" ? "danger" : "primary"}`}
                disabled={
                  actionLoading ||
                  (modal.type === "ban" && !modalNote) ||
                  (modal.type === "interview" && !modalNote) ||
                  (modal.type === "delete" && modalConfirm?.trim().toLowerCase() !== "delete")
                }
                onClick={() => {
                  if (modal.type === "approve")        updateApplicationStatus(modal.app.id, "approved",   modalNote);
                  else if (modal.type === "reject")    updateApplicationStatus(modal.app.id, "rejected",   modalNote);
                  else if (modal.type === "interview") updateApplicationStatus(modal.app.id, "interview",  modalNote);
                  else if (modal.type === "ban")       toggleBanMentor(modal.mentor);
                  else if (modal.type === "unban")     toggleBanMentor(modal.mentor);
                  else if (modal.type === "delete")    deleteMentor(modal.mentor);
                  else if (modal.type === "resolveReport") updateReport(modal.report.id, "resolved", modalNote);
                }}
              >
                {actionLoading ? <span className="adm-spin" /> : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Document Viewer Modal ── */}
      {docModal && (
        <div className="adm-modal-overlay" onClick={e => e.target === e.currentTarget && setDocModal(null)}>
          <div className="adm-doc-modal">
            <div className="adm-doc-header">
              <strong>{docModal.name}</strong>
              <button onClick={() => setDocModal(null)}
                style={{ background:"#f0f0f0",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:"1rem" }}>
                ✕
              </button>
            </div>
            <div className="adm-doc-body">
              <iframe
                className="adm-doc-iframe"
                src={docModal.url}
                title={docModal.name}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`adm-toast`} style={{
          background: toast.type === "error" ? "#f44336" : toast.type === "success" ? "#2e7d32" : "#1a237e"
        }}>
          {toast.msg}
        </div>
      )}
    </>
  );
}